
import React, { useMemo } from 'react'
import { Select } from 'antd'
import { Controller } from 'react-hook-form'
import { store } from '../../../store/store'
import { useParams } from 'react-router-dom'

export interface ICountUnits {
    value: string
    countUnits: number
}
type AreaGroup = {
    id: number
    name: string
    areas: string[]
    multiplier: number
    vipStatus?: boolean
    unitsCount?: number
}

type Props = {
    control: any
    data: { id: string; required?: boolean; content?: string[] }
    maxAreas?: number | string
    propertyID?: number | string
    unitID?: number | string
}
export default function CustomSelectAreasChange({ control, data }: Props): JSX.Element {
    const { objectID } = useParams()

    const actualAreas = (store.getState().app.allAreas as AreaGroup[]) ?? []

    const property = store.getState().property.properties.find((property) => property.id === Number(objectID))
    const defaultArea = property?.attributes.Area ?? ''
    const NAME_BREAK_AT = 23

    const sortedGroups = useMemo(
        () => actualAreas.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        [actualAreas]
    )


    const splitNameBySlashSpaced = (name: string) =>
        name.split(' / ').map(s => s.trim()).filter(Boolean);

    const normalizeText = (value: string): string =>
        value
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ')

    const filterAreaOption = (input: string, option?: { label?: React.ReactNode }): boolean => {
        const normalizedInput = normalizeText(input)
        if (normalizedInput.length === 0) return true
        const label = String(option?.label ?? '')
        return normalizeText(label).includes(normalizedInput)
    }

    return (
        <Controller
            name={data.id}
            control={control}
            rules={
                data.required
                    ? { required: 'Please select your preferred areas to proceed' }
                    : {}
            }
            render={({ field }) => (
                <Select
                    {...field}
                    size="large"
                    className="d-block"
                    listHeight={560}
                    showSearch
                    filterOption={filterAreaOption}
                    placeholder={data.content?.[1] ?? 'Select areas'}
                    value={field.value ?? defaultArea}
                    onChange={(val) => field.onChange(val)}
                    optionLabelProp="label"
                    popupClassName="drp-style"
                    defaultValue={defaultArea}
                >
                    {sortedGroups.map((group) => {
                        const isLong = (group.name ?? '').length > NAME_BREAK_AT
                        return (
                            <Select.Option
                                key={`group-${group.id}`}
                                value={group.name}
                                label={group.name}
                            >
                                <div style={{ display: 'grid', gap: 6 }}>
                                    {!isLong ? (
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {group.name}
                                        </span>
                                    ) : (
                                        <div style={{ fontWeight: 600, lineHeight: 1.15 }}>
                                            {splitNameBySlashSpaced(group.name).map((part, idx) => (
                                                <div key={idx}>{part}</div>
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {group.areas.map((a) => (
                                            <span
                                                key={`tag-${group.id}-${a}`}
                                                className="badge border border-primary badge-light-primary fs-8"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                {a}
                                            </span>
                                        ))}
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: 8,
                                        }}
                                    >
                                        {typeof group.unitsCount === 'number' && (
                                            <span className="badge border border-info badge-light-info fs-8">
                                                {group.unitsCount} Unit(s)
                                            </span>
                                        )}
                                        {group.vipStatus && (
                                            <span className="badge border border-warning badge-light-warning fs-8">
                                                VIP
                                            </span>
                                        )}
                                    </div>

                                    <div className="separator border border-bso border-1 px-8 my-1" />
                                </div>
                            </Select.Option>
                        )
                    })}
                </Select>
            )}
        />
    )
}

